import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

/* ================================================================== */
/*  LifeCraft4U core primitives. Every component is theme-agnostic:      */
/*  colors resolve through the .lc-dark / .lc-light variable layer.    */
/* ================================================================== */

/* ---- Button ---------------------------------------------------------- */
export type ButtonVariant = 'primary' | 'secondary' | 'tonal' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BTN_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-lime text-night',
  secondary: 'lc-glass text-lc-ink',
  tonal: 'bg-violet text-white',
  ghost: 'label !px-0 text-lc-mute hover:text-lc-ink',
}
const BTN_SIZE: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-[14px]',
  md: 'px-6 py-3 text-[15px]',
  lg: 'px-8 py-4 text-[17px]',
}

export function Button({
  variant = 'primary',
  size = 'md',
  href,
  to,
  className,
  children,
  ...rest
}: {
  variant?: ButtonVariant
  size?: ButtonSize
  href?: string
  to?: string
  className?: string
  children: React.ReactNode
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = cn(
    'inline-flex items-center justify-center gap-2 rounded-pill font-bold transition-transform duration-300 ease-brand',
    'hover:scale-[1.02] active:scale-[0.98] motion-reduce:hover:scale-100',
    BTN_VARIANT[variant],
    variant !== 'ghost' && BTN_SIZE[size],
    className,
  )
  if (to) return <Link to={to} className={cls}>{children}</Link>
  if (href) return <a href={href} className={cls}>{children}</a>
  return <button className={cls} {...rest}>{children}</button>
}

/* ---- Chip ------------------------------------------------------------ */
export type ChipTone = 'info' | 'glass' | 'lime' | 'coral' | 'outline'

const CHIP_TONE: Record<ChipTone, string> = {
  info: 'bg-cobalt text-white',
  glass: 'lc-glass-soft text-lc-ink',
  lime: 'bg-lime text-night',
  coral: 'bg-coral text-white',
  outline: 'border border-lc-line text-lc-mute',
}

export function Chip({ tone = 'glass', small, className, children }: { tone?: ChipTone; small?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-pill font-semibold', small ? 'px-2.5 py-1 text-[12px]' : 'px-3.5 py-1.5 text-[13px]', CHIP_TONE[tone], className)}>
      {children}
    </span>
  )
}

/* ---- Divider --------------------------------------------------------- */
export function Divider({ children, className }: { children?: React.ReactNode; className?: string }) {
  if (!children) return <hr className={cn('border-lc-line', className)} />
  return (
    <div className={cn('flex items-center gap-4', className)} role="separator">
      <span className="h-px flex-1 bg-lc-line" />
      <span className="label text-lc-mute">{children}</span>
      <span className="h-px flex-1 bg-lc-line" />
    </div>
  )
}

/* ---- SearchInput ----------------------------------------------------- */
export function SearchInput({ placeholder = 'Search communities', className, ...rest }: { placeholder?: string; className?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={cn('lc-glass-soft flex items-center gap-3 rounded-pill px-4 py-2.5 transition-shadow focus-within:ring-2 focus-within:ring-lc-accent/70', className)}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="shrink-0 text-lc-mute" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        placeholder={placeholder}
        className="w-full bg-transparent text-[15px] text-lc-ink outline-none placeholder:text-lc-mute"
        {...rest}
      />
    </label>
  )
}

/* ---- SectionHeading -------------------------------------------------- */
export function SectionHeading({ kicker, title, intro, center, className }: { kicker?: string; title: string; intro?: string; center?: boolean; className?: string }) {
  return (
    <div className={cn(center && 'text-center', className)}>
      {kicker && <p className="label text-lc-mute">{kicker}</p>}
      <h2 className="mt-3 font-display text-4xl text-lc-ink">{title}</h2>
      {intro && <p className={cn('mt-4 max-w-xl text-lg leading-relaxed text-lc-mute', center && 'mx-auto')}>{intro}</p>}
    </div>
  )
}

/* ---- ParticipationNote ------------------------------------------------ */
/* Standard community-participation note shown near every "Join" CTA across
   the site. Kept in one place so the wording stays consistent. */
export function ParticipationNote({ className }: { className?: string }) {
  return (
    <p className={cn('max-w-md text-[13px] leading-relaxed text-lc-mute', className)}>
      Free to join for a limited period. Take part in discussions, comment on posts and share your photos and videos.
    </p>
  )
}
